/**
 * What every figure on the radar means, behind a heading.
 *
 * Collapsed for the same reason Role Prioritisation collapses its prose: none of it is needed to
 * use the tab, all of it is needed the first time somebody disagrees with a light, and left open it
 * competes with the roles. The windows are read from the payload rather than restated here, so a
 * change to the SQL cannot leave this text quietly lying about what was measured.
 */
import {
  CollapsedSection,
  Prose,
  SubHead,
} from '../rolePrioritisation/CollapsedSection';
import type { RadarBenchmarks, RadarWindows } from '../../../types/taBottleneck';
import {
  MIN_COHORT,
  PIPELINE_MULTIPLIER,
  RAMP_WEEKS,
  TARGET_FILL_WEEKS,
  TIME_REFERENCE,
  windowLabel,
} from '../../../utils/bottleneckRadar';
import { Dot } from './radarUi';

const pct = (v: number | null) => (v == null ? '–' : `${Math.round(v * 10) / 10}%`);

export function RadarLegend({
  benchmarks,
  windows,
}: {
  benchmarks: RadarBenchmarks;
  windows: RadarWindows;
}) {
  return (
    <CollapsedSection number="04" title="How the lights are decided">
      <div className="flex flex-wrap gap-4 pb-3 text-[13px] text-asphalt">
        <span className="inline-flex items-center gap-1.5">
          <Dot status="green" /> On track
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Dot status="yellow" /> At risk
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Dot status="red" /> Off
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Dot status="unknown" /> Not enough data
        </span>
      </div>

      <SubHead>Thresholds (green / amber)</SubHead>
      <Prose>
        Pipeline coverage is qualified candidates ÷ (open hires × {PIPELINE_MULTIPLIER}), green at
        100%, amber at 60%. A candidate is qualified while they are active and at or past the first
        interview: in any interview, case, final or offer stage in Lever. Interview throughput against target is the same 100% / 60%. A conversion
        is measured against FINN&apos;s own rate, green at 100% of it, amber at 80%. Offer acceptance
        is green at 80%, amber at 60%.
      </Prose>
      <Prose>
        Phase 1 is blended rather than worst-of: a full pipeline stays green however it converts, and
        a thin one converting well is amber rather than red. Every other phase is as bad as its worst
        signal. The role&apos;s own light is a weighted average of the phases that could be judged, so
        one weak phase does not read as a crisis — and the bottleneck named on the line is the FIRST
        red, or failing that the first amber, because fixing a late phase while an early one starves
        it achieves nothing.
      </Prose>
      <Prose>
        A role open {RAMP_WEEKS} weeks or fewer is marked <strong>Ramp-up</strong> and its reds are
        softened to amber — its pipeline has not had a full cycle. Greens and ambers are untouched.
      </Prose>

      <SubHead>Where &quot;expected&quot; comes from</SubHead>
      <Prose>
        FINN-wide, over the same span as the role it is compared against — that is the whole point
        of it being one number. Applicant → first interview {pct(benchmarks.conv1)} over the last{' '}
        {windows.appliedCohortDays} days, first interview → case {pct(benchmarks.conv2)} and case →
        offer {pct(benchmarks.conv3)} over {windowLabel(windows.conversionDays).toLowerCase()}, offer
        acceptance {pct(benchmarks.accept)} year to date. A role is asked whether it behaves like
        FINN roles behave, not whether it beats an industry number.
      </Prose>
      <Prose>
        Interview targets are a plan, not a measurement: open hires worked backwards through those
        same benchmark rates, spread over {TARGET_FILL_WEEKS} weeks. The benchmark is used rather than
        the role&apos;s own conversion on purpose — a target built on a role&apos;s actual rate would
        quietly ask a badly converting role for fewer interviews.
      </Prose>

      <SubHead>When the radar refuses to judge</SubHead>
      <Prose>
        A conversion measured on fewer than {MIN_COHORT} candidates is shown with its cohort size and
        greyed out. It colours nothing and can never be named as a bottleneck. A phase with nothing
        judgeable is grey, and a grey phase is left out of the role&apos;s average rather than scored
        as a half pass — a role we cannot yet read must not drag down one we can.
      </Prose>
      <Prose>
        Before going grey, the two interview conversions look at a <strong>wider span</strong>:
        interviews are rare per role, so a window holding four cases often hides a role that has run
        twenty this year. They fall back to the year to date, scored against FINN&apos;s year rather
        than FINN&apos;s window, and the sub-label names both spans. The fallback only ever widens,
        so at 12 months or all history nothing falls back — the year to date is the narrower span
        there. Applicant → first interview has no fallback: applications are plentiful, and widening
        that one was measured as moving rates without making a single extra role judgeable.
      </Prose>
      <Prose>
        <strong>Offer acceptance falls back too, and on its own terms.</strong> It starts from the
        last {windows.offerDays} days rather than from the window above — it is a planning rate, not
        a figure the window controls — and it is judged against fixed thresholds rather than against
        FINN&apos;s rate, so its fallback to the year still applies whatever the window is set to.
      </Prose>

      <SubHead>Time signals are shown but do not drive the lights</SubHead>
      <Prose>
        Every duration on the cards is a real median over completed transitions. None of them changes
        a colour. Turning them on would change which roles read as broken, which is a decision to take
        deliberately rather than a side effect of the data becoming available. Reference thresholds,
        green / amber, in days: time to first interview{' '}
        {TIME_REFERENCE.daysToFirstInterview.join(' / ')} · first interview → case{' '}
        {TIME_REFERENCE.s2StageDays.join(' / ')} · case → offer {TIME_REFERENCE.s3StageDays.join(' / ')}{' '}
        · feedback {TIME_REFERENCE.feedbackDays.join(' / ')} · decision{' '}
        {TIME_REFERENCE.decisionDays.join(' / ')}.
      </Prose>

      <SubHead>What was measured, and over what window</SubHead>
      <Prose>
        Stocks — open hires, weeks open, active qualified — are a snapshot of now. Open hires is a
        count of open hiring-plan rows on the posting, which is why a posting hiring nine people is
        judged as one hiring nine people. Rates — inflow and interviews per week — are the counts
        for the previous full week, Monday to Sunday. Times are medians over the last {windows.timeDays}{' '}
        days and only over transitions that completed.
      </Prose>
      <Prose>
        Conversions are measured on a mature cohort: candidates who entered the stage at least{' '}
        {windows.maturityDays} days ago, so someone who applied last week is not counted as a failure
        to convert. The applicant cohort runs from {windows.appliedCohortDays} to{' '}
        {windows.maturityDays} days back. Offers are those resolved in the last {windows.offerDays}{' '}
        days. The warehouse ETL is nightly, so &quot;Re-read&quot; re-queries the warehouse — it
        cannot make the warehouse re-read Lever.
      </Prose>
    </CollapsedSection>
  );
}
